import { deepExtract } from 'helpers/deep';
import { isPresent } from 'helpers/verify';

const TOGGLE_FILTER_COLLAPSE = 'classpass/filters/TOGGLE_FILTER_COLLAPSE';
const SET_CLEAN = 'classpass/filters/SET_CLEAN';
const RESET_NEEDS_UPDATE = 'classpass/filters/RESET_NEEDS_UPDATE';
const SET_FILTERS_EDITING = 'classpass/filters/SET_FILTERS_EDITING';
const ADD_FILTER = 'classpass/filters/ADD_FILTER';
const CLEAR_FILTER = 'classpass/filters/CLEAR_FILTER';
const CLEAR_FILTERS = 'classpass/filters/CLEAR_FILTERS';
const TOGGLE_ITEM_ID = 'classpass/filters/TOGGLE_ITEM_ID';
const TOGGLE_FILTER_PARENT_COLLAPSE = 'classpass/filters/TOGGLE_FILTER_PARENT_COLLAPSE';
const SET_FILTER_VALUE = 'classpass/filters/SET_FILTER_VALUE';
const SET_ITEM_ID_SELECTED = 'classpass/filters/SET_ITEM_ID_SELECTED';

export const FILTER_WRITE_ACTIONS = [
  CLEAR_FILTER,
  CLEAR_FILTERS,
  TOGGLE_ITEM_ID,
  SET_FILTER_VALUE,
  SET_ITEM_ID_SELECTED,
];

const toggleFilterCollapse = (filterSetId, filter) => ({ type: TOGGLE_FILTER_COLLAPSE, data: { filterSetId, filter } });
const setMultipleFiltersEditing = (filterSetId, filters, isEditing) => ({
  type: SET_FILTERS_EDITING,
  data: {
    filterSetId,
    filters,
    isEditing,
  },
});
const setFilterEditing = (filterSetId, filter, isEditing) =>
  setMultipleFiltersEditing(filterSetId, [filter], isEditing);
const addFilter = (filterSetId, filter, items, relationships, options) => ({
  type: ADD_FILTER,
  data: {
    filterSetId,
    filter,
    items,
    relationships,
    options,
  },
});
const setClean = filterSetId => ({ type: SET_CLEAN, data: { filterSetId } });
const resetNeedsUpdate = (filterSetId, filters) => ({ type: RESET_NEEDS_UPDATE, data: { filterSetId, filters } });
const clearFilter = (filterSetId, filter, filtersToOmit, options) => ({
  type: CLEAR_FILTER,
  data: { filterSetId, filter, filtersToOmit, options },
});
const clearMultipleFilters = (filterSetId, filters) => ({
  type: CLEAR_FILTERS,
  data: {
    filterSetId,
    filters,
  },
});
const clearFilterSet = (filterSetId, filtersToOmit) => clearFilter(filterSetId, null, filtersToOmit);
const toggleItemId = (filterSetId, filter, ...itemIds) => ({
  type: TOGGLE_ITEM_ID,
  data: { filterSetId, filter, itemIds },
});
const toggleItemIdWithoutUpdate = (filterSetId, filter, ...itemIds) => ({
  type: TOGGLE_ITEM_ID,
  data: {
    filterSetId,
    filter,
    itemIds,
    options: {
      excludeFromWriteActions: true,
    },
  },
});
const setItemIdSelected = (filterSetId, filter, ...itemIds) => ({
  type: SET_ITEM_ID_SELECTED,
  data: { filterSetId, filter, itemIds },
});
const setFilterValue = (filterSetId, filter, value, options) => ({
  type: SET_FILTER_VALUE,
  data: {
    filterSetId,
    filter,
    value,
    options,
  },
});

const toggleFilterParentCollapse = (filterSetId, filter, itemId) => ({
  type: TOGGLE_FILTER_PARENT_COLLAPSE,
  data: { filterSetId, filter, itemId },
});

export const actionsForFilterSet = filterSetId => ({
  filterSet: filterSetId,
  toggleFilterCollapse: toggleFilterCollapse.bind(null, filterSetId),
  setMultipleFiltersEditing: setMultipleFiltersEditing.bind(null, filterSetId),
  setFilterEditing: setFilterEditing.bind(null, filterSetId),
  addFilter: addFilter.bind(null, filterSetId),
  setClean: setClean.bind(null, filterSetId),
  resetNeedsUpdate: resetNeedsUpdate.bind(null, filterSetId),
  clearFilter: clearFilter.bind(null, filterSetId),
  clearMultipleFilters: clearMultipleFilters.bind(null, filterSetId),
  clearFilterSet: clearFilterSet.bind(null, filterSetId),
  toggleItemId: toggleItemId.bind(null, filterSetId),
  toggleItemIdWithoutUpdate: toggleItemIdWithoutUpdate.bind(null, filterSetId),
  setItemIdSelected: setItemIdSelected.bind(null, filterSetId),
  setFilterValue: setFilterValue.bind(null, filterSetId),
  toggleFilterParentCollapse: toggleFilterParentCollapse.bind(null, filterSetId),
});

const initialState = {};

const initialFilterSetState = {
  filters: {},
  dirty: false,
  filtered: false,
};

const initialFilterState = {
  itemsById: {},
  parentedItemIds: {},
  countSelected: 0,
  itemIdSelected: {},
  parentIdPartialSelected: {},
  parentIdExpanded: {},
  expanded: false,
  filtered: false,
  needsUpdate: false,
  isEditing: false,
};

const recursivelyGetChildIds = (map, start) =>
  map[start].reduce(
    (children, child) => children.concat(map[child] ? recursivelyGetChildIds(map, child) : [child]),
    []
  );

const calculateParentStates = (parentMap, itemIdSelected) =>
  Object.keys(parentMap)
    .filter(parent => parent !== '0' && parent !== 'root')
    .reduce(
      (memo, parentId) => {
        const childIds = recursivelyGetChildIds(parentMap, parentId);
        if (childIds.some(child => itemIdSelected[child])) {
          memo.parentIdPartialSelected[parentId] = true;
        }
        return memo;
      },
      {
        parentIdPartialSelected: {},
      }
    );

const filtered = filter =>
  !!Object.keys(filter.itemIdSelected || {}).length ||
  (!!filter.isValueFilter && isPresent(filter.value) && filter.value !== filter.unfilteredValue);

const filteredSet = filters => Object.keys(filters || {}).some(filter => filters[filter].filtered);

const setFilterFilteredFlag = (filter, state) => ({
  ...state,
  [filter]: {
    ...state[filter],
    filtered: filtered(state[filter]),
  },
});

const setFilterSetFilteredFlag = (filterSetId, state) => ({
  ...state,
  [filterSetId]: {
    ...state[filterSetId],
    filtered: filteredSet(state[filterSetId].filters),
  },
});

export default function reducer(state = initialState, action = {}) {
  const { filtersToOmit = [], filterSetId, filter, filters, itemId, itemIds, options, value } = action.data || {};

  switch (action.type) {
    // TODO: Split up filter creator to have creators for different types of filters and avoid extra fields polluting fitler data
    case ADD_FILTER:
      return {
        ...state,
        [filterSetId]: {
          ...(state[filterSetId] || initialFilterSetState),
          filters: {
            ...setFilterFilteredFlag(filter, {
              ...(state[filterSetId] ? state[filterSetId].filters : {}),
              [filter]: {
                ...initialFilterState,
                ...(state[filterSetId] ? state[filterSetId].filters[filter] : {}),
                exclusive: options && options.exclusive,
                filterable: options && options.filterable,
                isValueFilter: options && options.valueFilter,
                name: options && options.name,
                value: options && options.value,
                unfilteredValue: options && options.unfilteredValue, // used as indicator for "filtered" flag if there is a value that represents an "unfiltered" state
                itemIdSelected: (options && !options.exclusive && options.selected) || {},
                countSelected: options && Object.keys(options.selected || {}).length,
                itemsById: action.data.items.reduce((memo, item) => ({ ...memo, [item.id]: item }), {}),
                orderedItemIds: action.data.items.map(item => item.id),
                parentedItemIds: action.data.relationships
                  ? action.data.relationships
                  : { root: action.data.items.map(item => item.id) },
              },
            }),
          },
        },
      };
    case SET_FILTER_VALUE:
      return setFilterSetFilteredFlag(filterSetId, {
        ...state,
        [filterSetId]: {
          ...state[filterSetId],
          dirty: true,
          filters: {
            ...setFilterFilteredFlag(filter, {
              ...state[filterSetId].filters,
              [filter]: {
                ...state[filterSetId].filters[filter],
                value,
                dirty: true,
                needsUpdate: true,
                isEditing:
                  action.data.options && action.data.options.resetIsEditing
                    ? false
                    : state[filterSetId].filters[filter]?.isEditing,
              },
            }),
          },
        },
      });
    case SET_ITEM_ID_SELECTED: {
      const itemIdSelected = itemIds.reduce((object, id) => {
        object[id] = true;
        return object;
      }, {});

      return setFilterSetFilteredFlag(filterSetId, {
        ...state,
        [filterSetId]: {
          ...state[filterSetId],
          dirty: true,
          filters: {
            ...setFilterFilteredFlag(filter, {
              ...state[filterSetId].filters,
              [filter]: {
                ...state[filterSetId].filters[filter],
                itemIdSelected,
                countSelected: Object.keys(itemIdSelected).length,
                ...calculateParentStates(state[filterSetId].filters[filter].parentedItemIds, itemIdSelected),
              },
            }),
          },
        },
      });
    }
    case TOGGLE_FILTER_COLLAPSE:
      return {
        ...state,
        [filterSetId]: {
          ...state[filterSetId],
          filters: Object.keys(state[filterSetId].filters).reduce((set, thisFilterId) => {
            const existingExpandedStatus = state[filterSetId].filters[thisFilterId].expanded;
            const nextExpandedStatus = !existingExpandedStatus;

            return {
              ...set,
              [thisFilterId]: {
                ...state[filterSetId].filters[thisFilterId],
                expanded: !filter || thisFilterId === filter ? nextExpandedStatus : existingExpandedStatus,
              },
            };
          }, {}),
        },
      };
    case SET_FILTERS_EDITING:
      return {
        ...state,
        [filterSetId]: {
          ...state[filterSetId],
          filters: {
            ...state[filterSetId].filters,
            ...filters.reduce(
              (filterAccumulator, filterName) => ({
                ...filterAccumulator,
                [filterName]: {
                  ...state[filterSetId].filters[filterName],
                  isEditing: filters.includes(filterName)
                    ? action.data.isEditing
                    : state[filterSetId].filters[filterName]?.isEditing,
                },
              }),
              {}
            ),
          },
        },
      };
    case SET_CLEAN:
      return {
        ...state,
        [filterSetId]: {
          ...state[filterSetId],
          dirty: false,
        },
      };
    case RESET_NEEDS_UPDATE:
      return {
        ...state,
        [filterSetId]: {
          ...state[filterSetId],
          filters: {
            ...state[filterSetId].filters,
            ...filters.reduce(
              (set, filterName) => ({
                ...set,
                [filterName]: {
                  ...state[filterSetId].filters[filterName],
                  needsUpdate: false,
                },
              }),
              {}
            ),
          },
        },
      };
    case CLEAR_FILTER:
      return setFilterSetFilteredFlag(filterSetId, {
        ...state,
        [filterSetId]: {
          ...state[filterSetId],
          dirty: true,
          filters: Object.keys(deepExtract(`${filterSetId}.filters`, state, {})).reduce(
            (set, thisFilterId) => ({
              ...setFilterFilteredFlag(thisFilterId, {
                ...set,
                [thisFilterId]: {
                  ...state[filterSetId].filters[thisFilterId],
                  ...// Clears the current filter IF
                  ((!filter || // If no filter (eg when clearFilterSet called, thus clearing all filters)
                    thisFilterId === filter) && // OR if the filter being cleared matches the current filter // BUT don't clear any filters that are intentionally omitted
                  !filtersToOmit.includes(thisFilterId)
                    ? {
                        countSelected: 0,
                        itemIdSelected: {},
                        parentIdPartialSelected: {},
                        value: null,
                      }
                    : {}),
                },
              }),
            }),
            {}
          ),
        },
      });
    case CLEAR_FILTERS:
      return setFilterSetFilteredFlag(filterSetId, {
        ...state,
        [filterSetId]: {
          ...state[filterSetId],
          dirty: true,
          filters: Object.keys(deepExtract(`${filterSetId}.filters`, state, {})).reduce(
            (set, thisFilterId) => ({
              ...setFilterFilteredFlag(thisFilterId, {
                ...set,
                [thisFilterId]: {
                  ...state[filterSetId].filters[thisFilterId],
                  ...// Clears the current filter IF
                  (filters.includes(thisFilterId)
                    ? {
                        countSelected: 0,
                        itemIdSelected: {},
                        parentIdPartialSelected: {},
                        value: null,
                      }
                    : {}),
                },
              }),
            }),
            {}
          ),
        },
      });
    case TOGGLE_ITEM_ID: {
      const currentFilterData = state[filterSetId].filters[filter];
      let itemIdSelected = { ...currentFilterData.itemIdSelected };

      itemIds.forEach(thisItemId => {
        if (currentFilterData.exclusive) {
          itemIdSelected = { [thisItemId]: true };
        } else if (currentFilterData.parentedItemIds[thisItemId]) {
          // Clicked Parent
          // Parent was selected, deselect it.
          if (itemIdSelected[thisItemId]) {
            delete itemIdSelected[thisItemId];
            // Parent was not selected/partially selected, deselect all children & select parent.
          } else {
            recursivelyGetChildIds(currentFilterData.parentedItemIds, thisItemId).forEach(
              item => delete itemIdSelected[item]
            );
            itemIdSelected[thisItemId] = true;
          }
          // Clicked selected child, deselect it.
        } else if (itemIdSelected[thisItemId]) {
          delete itemIdSelected[thisItemId];
          // Clicked deselected child, select it & deselect parent if it's selected.
        } else {
          const parentId = currentFilterData.itemsById[thisItemId].parent_id;
          if (itemIdSelected[parentId]) {
            delete itemIdSelected[parentId];
          }
          itemIdSelected[thisItemId] = true;
        }
      });

      return setFilterSetFilteredFlag(filterSetId, {
        ...state,
        [filterSetId]: {
          ...state[filterSetId],
          dirty: true,
          filters: {
            ...setFilterFilteredFlag(filter, {
              ...state[filterSetId].filters,
              [filter]: {
                ...state[filterSetId].filters[filter],
                itemIdSelected,
                needsUpdate: true,
                countSelected: Object.keys(itemIdSelected).length,
                ...calculateParentStates(state[filterSetId].filters[filter].parentedItemIds, itemIdSelected),
              },
            }),
          },
        },
      });
    }
    case TOGGLE_FILTER_PARENT_COLLAPSE: {
      const parentIdExpanded = { ...state[filterSetId].filters[filter].parentIdExpanded };

      if (parentIdExpanded[itemId]) {
        delete parentIdExpanded[itemId];
      } else {
        parentIdExpanded[itemId] = true;
      }
      return setFilterSetFilteredFlag(filterSetId, {
        ...state,
        [filterSetId]: {
          ...state[filterSetId],
          dirty: true,
          filters: {
            ...setFilterFilteredFlag(filter, {
              ...state[filterSetId].filters,
              [filter]: {
                ...state[filterSetId].filters[filter],
                parentIdExpanded,
              },
            }),
          },
        },
      });
    }
    default:
      return state;
  }
}
